import { Leaderboard, notification } from "@datagame/ui";
import { t } from "i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { CustomizedBackground } from "../CustomizedBackground";
import { GET_CURRENT_ROW, GET_LEADERBOARD } from "../../graphql/queries/Game";
import { LeaderboardConfiguration, WordopolisConfiguration, SwiperConfiguration } from "../../gql/graphql";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";

import styles from "./LeaderboardView.module.scss";

type GameWithLeaderboardConfiguration = (WordopolisConfiguration | SwiperConfiguration) & {
  leaderboardConfiguration: LeaderboardConfiguration;
};

export interface LeaderboardViewProps {
  gameId: string;
  responseId?: string;
  onContinue?: () => void;
}

export const LeaderboardView = (props: LeaderboardViewProps) => {
  const { projectId } = useParams();
  const { responseId, gameId, onContinue } = props;
  const { games } = useGlobalConfiguration();
  const game = games.find((game) => game.id === gameId);

  if (!game) {
    throw new Error(`Game with id ${gameId} not found`);
  }

  const { data: { gameLeaderboard = [] } = {} } = useQuery(GET_LEADERBOARD, {
    variables: {
      gameId,
    },
  });

  const { data: { currentRow } = {} } = useQuery(GET_CURRENT_ROW, {
    variables: {
      responseSlug: parseInt(responseId as string),
    },
    skip: !responseId,
  });

  const {
    leaderboardConfiguration,
    gameVisuals: { themeColor },
  } = game.configuration as GameWithLeaderboardConfiguration;

  const handleShare = () => {
    const shareData = {
      title: t("leaderboard:share.title"),
      text: t("leaderboard:share.text"),
      url: `${window.location.origin}/${projectId}/${gameId}/leaderboard`,
    };
    if (navigator.share) {
      navigator.share(shareData);
    } else {
      notification.success({
        title: t("leaderboard:share.notification.title"),
        description: t("leaderboard:share.notification.description"),
      });
    }
    navigator.clipboard.writeText(shareData.url);
  };

  return (
    <CustomizedBackground config={leaderboardConfiguration} className={styles.leaderboardView}>
      <Leaderboard
        configuration={leaderboardConfiguration}
        themeColor={themeColor}
        results={gameLeaderboard}
        currentRow={currentRow}
        onShare={handleShare}
        onContinue={onContinue}
      />
    </CustomizedBackground>
  );
};
